import React from 'react';
import {
  CheckCircle2,
  ExternalLink,
  FileText,
  ShieldCheck,
  Sparkles,
  WalletCards
} from 'lucide-react';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { PageHero } from '../components/shared/PageHero';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';

const insuranceUrl = 'https://www.segurabici.es/zwheel/';

const coverageHighlights = [
  {
    title: 'Coberturas adaptadas',
    description: 'Opciones pensadas para vehículos de movilidad personal y el uso diario del patinete eléctrico.',
    icon: ShieldCheck
  },
  {
    title: 'Protección y responsabilidad civil',
    description: 'Información sobre protección frente a daños y responsabilidad civil ante posibles imprevistos.',
    icon: CheckCircle2
  },
  {
    title: 'Contratación sencilla',
    description: 'Consulta de forma clara las condiciones, precios y opciones disponibles para clientes Zwheel.',
    icon: FileText
  }
];

export default function Seguros() {
  useSEO({
    title: 'Seguros para Patinetes Eléctricos',
    description: 'Consulta las opciones de seguro disponibles para clientes Zwheel, con coberturas adaptadas a vehículos de movilidad personal.',
    keywords: 'seguro patinete eléctrico, seguro VMP, seguro Zwheel, responsabilidad civil patinete'
  });

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        tag="Seguros VMP"
        title="Seguros para patinetes eléctricos"
        subtitle="Información clara sobre coberturas, condiciones y precios para circular con mayor tranquilidad."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-max section-padding">
          <div className="mx-auto max-w-6xl space-y-10">
            <AnimatedSection>
              <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
                <div className="rounded-[2rem] border border-gray-200 bg-[color:var(--zw-ivory)] p-8 md:p-10">
                  <span className="mb-3 block text-sm font-bold uppercase tracking-[0.18em] text-[color:var(--zw-moss)]">
                    Movilidad con tranquilidad
                  </span>
                  <h2 className="mb-5 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                    Protege tu vehículo antes de salir a circular
                  </h2>
                  <div className="space-y-5 text-lg leading-8 text-gray-700">
                    <p>
                      En Zwheel queremos que disfrutes de tu movilidad con total tranquilidad. Por eso,
                      ponemos a tu disposición información clara y detallada sobre seguros disponibles
                      para tu vehículo.
                    </p>
                    <p>
                      Este seguro está especialmente pensado para ofrecerte protección ante posibles
                      imprevistos y ayudarte a circular con mayor seguridad y confianza.
                    </p>
                  </div>
                </div>

                <div className="rounded-[2rem] border border-gray-200 bg-white p-8 shadow-sm md:p-10">
                  <div className="mb-6 flex items-center gap-3">
                    <WalletCards className="h-7 w-7 text-black" />
                    <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
                      Consulta las opciones disponibles
                    </h2>
                  </div>
                  <p className="mb-8 text-base leading-7 text-gray-700">
                    En el botón que encontrarás a continuación podrás consultar todas las opciones de
                    cobertura, condiciones y precios de los seguros que ofertamos para nuestros clientes.
                  </p>
                  <Button asChild className="h-12 rounded-full bg-black px-7 text-white hover:bg-gray-800">
                    <a href={insuranceUrl} target="_blank" rel="noopener noreferrer">
                      ¡Contrata tu seguro!
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.08}>
              <div className="grid gap-5 md:grid-cols-3">
                {coverageHighlights.map((item) => (
                  <div key={item.title} className="rounded-[1.5rem] border border-gray-200 bg-white p-6 shadow-sm">
                    <item.icon className="mb-5 h-7 w-7 text-black" />
                    <h3 className="mb-3 text-xl font-bold text-gray-900">{item.title}</h3>
                    <p className="text-sm leading-6 text-gray-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.14}>
              <div className="rounded-[2rem] border border-gray-200 bg-[color:var(--zw-sand)] p-8 md:p-10">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-3xl">
                    <div className="mb-4 flex items-center gap-3">
                      <Sparkles className="h-7 w-7 text-black" />
                      <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
                        Haz clic para acceder a toda la información
                      </h2>
                    </div>
                    <p className="text-base leading-7 text-gray-700">
                      Revisa las coberturas disponibles, compara condiciones y completa la contratación
                      desde la plataforma de seguro para clientes Zwheel.
                    </p>
                    <p className="mt-4 text-lg font-semibold text-gray-900">
                      Y disfruta de conducir seguro con tu patinete eléctrico.
                    </p>
                  </div>

                  <Button asChild variant="outline" className="bg-white">
                    <a href={insuranceUrl} target="_blank" rel="noopener noreferrer">
                      Ver seguro
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
